import { useTranslation } from 'react-i18next';
import React from 'react';
import { Box, Button, Typography } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { InfoButton } from '../FormWidgets/Description';
import useLightStyles from './lightChannelStyles';
import LightingSection from '../Tabs/Lights/LightingSection';
import { resolveLightProfile } from '../Utils/light_profile_source';
import {
  defaultLightProfile,
  lightCycle,
  phaseLightErrors,
  lightPhaseValuesKnown,
  lightCatalogErrors,
  missingPhaseLightChannels
} from '../Utils/light_profiles';
import {
  emptyPhaseLighting,
  phaseLightingCatalog,
  phaseLightingSelected,
  phaseLightingValue,
  changePhaseLighting
} from '../Utils/light_phase_utils';

export default function ScheduledLightChannels({
  phase,
  values,
  index,
  controls,
  parent,
  controller,
  recoveryOnly = false
}) {
  const { t } = useTranslation();
  const classes = useLightStyles();
  const previous = parent.settings?.scheduling?.schedule_values?.scheduling_values?.find(
    (entry) => entry.phase_id === phase.phase_id
  );
  if (
    !lightPhaseValuesKnown(phase.lighting, { allowRangeErrors: true }) ||
    !lightPhaseValuesKnown(previous?.lighting)
  )
    return <Alert severity="warning">{t('lighting_unknown_settings')}</Alert>;
  const lighting = phase.lighting?.schema_version === 1 ? phase.lighting : emptyPhaseLighting();
  const zone = Number(parent.zone_id);
  const catalog = controller.settings.light_channels;
  const effective = phaseLightingCatalog(catalog, zone, lighting);
  const baseline = phaseLightingCatalog(catalog, zone, previous?.lighting);
  const installations = catalog.channels.filter((channel) => channel.zone === zone);
  const cycle = lightCycle(
    parent.settings.timing,
    values.schedule_meta.schedule_day_night_enabled ? phase : null
  );
  const disabled = !controller.access_rights?.scheduling?.write;
  const write = (next, saveTemp = true) =>
    controls.setValue(`schedule_values.scheduling_values[${index}].lighting`, next, saveTemp);
  const pointCount = lighting.channels.reduce((count, channel) => count + channel.points.length, 0);
  const errors = phaseLightErrors(lighting, cycle, installations, previous?.lighting);
  const missingChannels = missingPhaseLightChannels(lighting, installations);
  const change = (field, identity, value, saveTemp) => {
    // Replay buttons use the ordinary slider callbacks too. An inherited field
    // can only become an override through its explicit selection checkbox.
    if (disabled || !phaseLightingSelected(lighting, field, identity)) return;
    write(
      changePhaseLighting(
        lighting,
        field,
        identity,
        value,
        field !== 'channel_profile' || value.length > 0
      ),
      saveTemp
    );
  };
  const select = (field, identity, selected) => {
    let value = phaseLightingValue(effective, zone, field, identity);
    if (field === 'channel_profile' && selected) {
      const source = resolveLightProfile(
        identity,
        effective.channels,
        effective.subzones,
        effective.zones.find((entry) => entry.zone === zone)
      );
      value = source.kind === 'custom' ? source.points : defaultLightProfile();
    }
    write(changePhaseLighting(lighting, field, identity, value, selected));
  };
  const missingChannelControls = missingChannels.length > 0 && (
    <Box my={2}>
      <Alert severity="warning">{t('lighting_missing_channels_help')}</Alert>
      {missingChannels.map((missing) => (
        <Box key={`${missing.subzone}:${missing.channel}`} className={classes.actions}>
          <Typography>
            {t('lighting_missing_channel', {
              subzone: missing.subzone + 1,
              channel: missing.channel + 1
            })}
          </Typography>
          <Button
            color="primary"
            variant="outlined"
            disabled={disabled}
            onClick={() =>
              write({
                ...lighting,
                channels: lighting.channels.filter((channel) => channel !== missing)
              })
            }
          >
            {t('lighting_remove_missing_channel')}
          </Button>
        </Box>
      ))}
    </Box>
  );
  if (recoveryOnly) return missingChannelControls || null;
  // Apply the ordinary editor's existing unknown-settings protection before
  // inheriting installation values. Retained missing-channel recovery stays usable.
  if (lightCatalogErrors(catalog, {}, catalog).length)
    return (
      <>
        {missingChannelControls}
        <Alert severity="warning">{t('lighting_unknown_settings')}</Alert>
      </>
    );
  return (
    <LightingSection
      controller={controller}
      catalog={effective}
      baseline={baseline}
      zone={zone}
      cycle={cycle}
      writable={!disabled}
      sunriseDuration={parent.settings.lights?.light_sunrise_duration ?? 0}
      subzoneCount={Number(parent.settings.lights?.lights_sub_zones_active_cnt ?? 4)}
      idPrefix={`phase-light-${phase.phase_id}`}
      profilePointCount={pointCount}
      phaseControls={{
        selected: (field, identity) => phaseLightingSelected(lighting, field, identity),
        change,
        select,
        points: (identity) =>
          lighting.channels.find(
            (channel) =>
              channel.subzone === identity.subzone &&
              channel.channel === identity.channel &&
              channel.kind === 1
          )?.points ?? []
      }}
      feedback={
        <>
          <InfoButton brief={t('lighting_phase_help')} content={t('lighting_phase_description')} />
          {missingChannelControls}
          {errors.map((error) => (
            <Alert key={error} severity="warning">
              {error}
            </Alert>
          ))}
        </>
      }
      footer={
        Boolean(effective.advanced_zone_mask & (1 << zone)) && (
          <Typography variant="body2">
            {t('lighting_phase_pool')} {pointCount}/256 {t('lighting_points')} /{' '}
            {lighting.channels.length}/24 {t('lighting_phase_channels')}
          </Typography>
        )
      }
    />
  );
}
